import type { ProjectQuotaStatus } from "../api/types";
import type { QuotaNotice } from "./quotaStatus";

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

const formatWhen = (iso: string | number) =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
const formatDay = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/** D4: pace from the server's own `as_of`/window, never the client clock. */
export function estimateDepletion(status: ProjectQuotaStatus): string | null {
  const usage = status.usage;
  if (!usage || usage.budget_tokens === null || usage.used_tokens <= 0)
    return null;
  if (usage.used_tokens >= usage.budget_tokens) return null;
  const asOf = Date.parse(status.as_of);
  const start = Date.parse(status.window_start);
  const end = Date.parse(status.window_end);
  const elapsed = asOf - start;
  if (!Number.isFinite(elapsed) || !Number.isFinite(end) || elapsed < HOUR_MS)
    return null;
  const rate = usage.used_tokens / elapsed;
  const eta = asOf + (usage.budget_tokens - usage.used_tokens) / rate;
  if (eta >= end) {
    return `At this pace the budget lasts until it resets on ${formatDay(status.window_end)}.`;
  }
  const left = eta - asOf;
  const hours = Math.round(left / HOUR_MS);
  const span =
    hours < 24
      ? plural(Math.max(1, hours), "hour")
      : plural(Math.round(left / DAY_MS), "day");
  return `At this pace it runs out in about ${span}, around ${formatWhen(eta)}.`;
}

export function describeProjectQuotaStatus(
  status: ProjectQuotaStatus,
): QuotaNotice | null {
  const level = status.level;
  if (level !== "warning" && level !== "hard_stop") return null; // D8
  if (!status.thresholds) return null; // D8: defensive against a malformed body
  const u = status.usage;
  const figures =
    u && u.budget_tokens !== null
      ? [
          `${u.used_tokens.toLocaleString("en-US")} of ${u.budget_tokens.toLocaleString("en-US")} tokens this month`,
        ]
      : [];
  if (level === "hard_stop") {
    return {
      level,
      figures,
      title: "AI is paused on this project",
      body: `This project has used its AI token budget for this month. Your work is saved; AI resumes ${formatWhen(status.window_end)}.`,
    };
  }
  const pace = estimateDepletion(status);
  return {
    level,
    figures,
    title: "This project is near its AI budget",
    body: `More than ${status.thresholds.warning_pct}% of this project's monthly AI token budget is used. AI keeps working until ${status.thresholds.hard_stop_pct}%; your work is never blocked.${pace ? ` ${pace}` : ""}`,
  };
}

export function projectQuotaDismissKey(
  status: ProjectQuotaStatus,
): string | null {
  if (status.level !== "warning" && status.level !== "hard_stop") return null;
  return `${status.project_id}:${status.level}:${status.window_start}`;
}
